import React, {
  useMemo,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useState,
  useEffect,
} from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  CellDateTimeV8,
  EyeColumnV8,
  CellFloatCustomV8,
  SelectColumnV8,
} from 'motion-components';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { getRouteByName } from 'app/routes';
import { FormatDateTime } from 'shared-pages/common/date-helpfunc';

/* ----------- Grid ------------------ */
const idFieldName = 'id';
const nameFieldName = 'identificationDocument';
const extraFieldName = 'netWeight';
export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    updateSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const columns = useMemo(
    () => [
      SelectColumnV8(),
      {
        id: 'identificationDocument',
        header: t('Κωδικός Εντύπου'),
        columnName: t('Κωδικός Εντύπου'),
        accessorKey: 'identificationDocument',
        orderByField: 'identificationDocument',
        cell: ({ getValue, row }) => (
          <div className="text-center">
            <Link to={`/idoc/${row.original.id}`}>
              {row.original.identificationDocument?.trimStart()}
            </Link>
          </div>
        ),
        size: 20,
      },
      {
        id: 'dateCollected',
        header: t('Ημερομηνία Συλλογής'),
        columnName: t('Ημερομηνία Συλλογής'),
        accessorKey: 'dateCollected',
        orderByField: 'dateCollected',
        cell: CellDateTimeV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'collectionPointName',
        header: t('Προέλευση Αποβλήτων'),
        columnName: t('Προέλευση Αποβλήτων'),
        accessorKey: 'collectionPointName',
        orderByField: 'collectionPointName',

        cell: ({ getValue, row }) => (
          <div className="text-start">
            <div>{row.original.collectionPointName}</div>
            <div style={{ color: 'gray', fontSize: 'smaller' }}>
              {' '}
              {row.original.colpointAddressLine1}
            </div>
          </div>
        ),
        size: 25,
        meta: { type: 'text' },
      },
      {
        id: 'collectorCompanyName',
        header: t('Συλλέκτης'),
        columnName: t('Συλλέκτης'),
        accessorKey: 'collectorCompanyName',
        orderByField: 'collectorCompanyName',
        cell: TableCellV8,
        size: 30,
        meta: { type: 'text' },
      },
      {
        id: 'vehPlateNumber',
        header: t('Όχημα'),
        columnName: t('Όχημα'),
        accessorKey: 'vehPlateNumber',
        orderByField: 'vehPlateNumber',
        cell: ({ getValue, row }) => (
          <div className="text-start">
            {row.original.vehPlateNumber}
            <div style={{ color: 'gray', fontSize: 'smaller' }}> {row.original.driverFullName}</div>
          </div>
        ),
        size: 20,
      },
      {
        id: 'wastereceptionpointCompanyName',
        header: t('Παραλαβή Αποβλήτων'),
        columnName: t('Παραλαβή Αποβλήτων'),
        accessorKey: 'wastereceptionpointCompanyName',
        orderByField: 'wastereceptionpointCompanyName',
        cell: TableCellV8,
        size: 30,
        meta: { type: 'text' },
      },
      {
        id: 'receptionNetWeight',
        header: t('Βάρος Αποβλήτων'),
        columnName: t('Βάρος Αποβλήτων'),
        accessorKey: 'receptionNetWeight',
        orderByField: 'receptionNetWeight',
        cell: CellFloatCustomV8(8, 1),
        size: 30,
        meta: { type: 'text' },
      },

      {
        id: 'dateStorage',
        header: t('Ημερομηνία Παράδοσης'),
        columnName: t('Ημερομηνία Παράδοσης'),
        accessorKey: 'dateStorage',
        orderByField: 'dateStorage',
        cell: CellDateTimeV8,
        size: 50,
        meta: { type: 'text' },
      },
      {
        id: 'finaldestinationCompanyName',
        header: t('Τελική Διάθεση Αποβλήτων'),
        columnName: t('Τελική Διάθεση Αποβλήτων'),
        accessorKey: 'finaldestinationCompanyName',
        orderByField: 'finaldestinationCompanyName',
        cell: ({ getValue, row }) => (
          <div className="text-start">
            {row.original.finaldestinationCompanyName}
            <div style={{ color: 'gray', fontSize: 'smaller' }}>
              {row.original.finaldestinationCompanyNoteDocNumber}
              {' - '}
              {FormatDateTime(row.original.finalDestinationDate)}
            </div>
          </div>
        ),
        size: 50,
        meta: { type: 'text' },
      },
      // isActiveColumn(t, i18n),
      // firstDateUserColumn(t),
      // lastDateUserColumn(t),
      /* ...(!isGridInForm && appStore.isPlatformOwner
        ? [
            {
              id: 'edit',
              header: t('Ενέργειες'),
              columnName: t('Ενέργειες'),
              accessorKey: 'edit',
              orderByField: 'lastUser',
              size: 120,
              cell: ({ row, table }) => {
                return true ? EditCellV8({ row, table }) : <>-</>;
              },
            },
          ]
        : []), */
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  const translateIds = (values) => {
    return values.reduce((acc, value) => ({ ...acc, [value.id]: true }), {});
  };

  const initRowSelection = selected ? translateIds(selected) : {};

  const onRowSelectionChange = (rowSelection) => {
    // rowSelection {id: true}
    // {11: true, 21: true, 32: true}
    const currentData = data; //data[Object.keys(data)[0]]?.data || [];

    const newSelection = Object.keys(rowSelection).reduce((acc, key) => {
      const isSelected = rowSelection[key];
      const row =
        currentData.find((item) => item[idFieldName].toString() === key) ||
        selected.find((item) => item[idFieldName].toString() === key);
      if (isSelected && row)
        acc.push({
          [idFieldName]: row[idFieldName],
          [nameFieldName]: row[nameFieldName],
          [extraFieldName]: row[extraFieldName],
        });
      return acc;
    }, []);

    setSelected((prev) => {
      // αφαίρεσε απο τα prev την τρέχουσα σελίδα
      prev = prev.filter((row, index, self) => {
        // find in data
        const existsRowIdx = data.findIndex((d) => d[idFieldName] === row[idFieldName]);
        return existsRowIdx === -1; // keep if not found in data
      });
      // concat
      const merged = [...prev, ...newSelection];
      // remove dups by id (keeps first occurrence)
      const uniqueById = merged.filter(
        (row, index, self) => index === self.findIndex((r) => r[idFieldName] === row[idFieldName]),
      );
      return uniqueById;
    });
  };

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          initRowSelection={initRowSelection}
          getRowId={(row) => row[idFieldName].toString()}
          onRowClick={(table) => {
            //const isSelected = !table.row.getIsSelected();
            //updateSelected(table.row.original, isSelected);
            //table.row.toggleSelected(isSelected);
          }}
          onRowSelectionChange={onRowSelectionChange}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
